import { useEffect, useState } from 'react';

import { IconClose } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { useCombobox } from '../../../common/hooks/use-combobox';
import SectionTabs from '../../navigation/section-tabs';
import { AUDIT_TABS } from '../../navigation/tabs';
import { useOpuSearch } from '../contexts/deletions';
import DeleteOpuModal from '../forms/delete-opu-modal';

import '../../../common/styles/catalog.css';
import '../styles/audit.css';

// Auditoría · Eliminaciones (admin-aspiration-sessions.md · 0127): borrado en cascada de una
// sesión OPU. El listado ya trae `counts` → preview de la cascada; `births_count>0` bloquea (409).
const CASCADE = [
    ['aspirations_count', 'Aspiraciones'],
    ['clivages_count',    'Clivages'],
    ['embryos_count',     'Embriones'],
    ['transfers_count',   'Transferencias'],
    ['pregnancies_count', 'Preñeces'],
];

function OpuSearch({ onSelect }) {
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [text]);

    const term = text.trim();
    const { data, isFetching } = useOpuSearch(debounced);
    const items = data?.items ?? [];
    const choose = (s) => { setText(''); onSelect?.(s); };
    const cb = useCombobox({ items, onChoose: choose });

    return (
        <div className="search">
            <input
                type="text" className="search__input" value={text} placeholder="Buscar sesión OPU por código (ej. OPU-12)…"
                role="combobox" aria-autocomplete="list" aria-expanded={cb.open} aria-controls={cb.listId} aria-activedescendant={cb.activeId}
                onChange={(e) => { setText(e.target.value); cb.setOpen(true); }}
                onFocus={cb.onFocus} onBlur={cb.onBlur} onKeyDown={cb.onKeyDown}
            />
            {cb.open && (
                <div className="search__panel" role="listbox" id={cb.listId}>
                    {term.length < 2 ? (
                        <p className="search__hint">Escribí al menos 2 caracteres.</p>
                    ) : !items.length ? (
                        <p className="search__hint">{isFetching ? 'Buscando…' : 'Sin resultados.'}</p>
                    ) : (
                        items.map((s, i) => (
                            <button
                                key={s.id} type="button" id={cb.optionId(i)} role="option" aria-selected={cb.activeIndex === i}
                                className={`search__item ${cb.activeIndex === i ? 'search__item--active' : ''}`}
                                onMouseDown={cb.onOptionMouseDown(s)} onMouseEnter={() => cb.setActive(i)}
                            >
                                <span className="search__item-body">
                                    <span className="search__item-name">{s.code}</span>
                                    <span className="search__item-sub">{[formatDateOnly(s.date), s.farm?.name].filter(Boolean).join(' · ') || '—'}</span>
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default function DeletionsPanel() {
    const [session, setSession] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const counts = session?.counts ?? {};
    const rows = CASCADE.map(([k, label]) => [label, Number(counts[k]) || 0]).filter(([, n]) => n > 0);
    const total = rows.reduce((a, [, n]) => a + n, 0);
    const maxCount = Math.max(1, ...rows.map(([, n]) => n));
    const births = Number(counts.births_count) || 0;
    const blocked = births > 0;

    return (
        <div className="cat-overview mrg">
            <SectionTabs tabs={AUDIT_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Auditoría</h1>
                </div>
            </header>

            <p className="mrg__lede">
                Eliminá una <strong>sesión OPU</strong> y toda su descendencia en cascada (aspiraciones, clivages, embriones,
                transferencias, preñeces). <strong>Acción sensible</strong>: soft-delete reversible sólo de forma manual, y
                bloqueada si ya hay un parto aguas abajo.
            </p>

            <div className="mrg-slot mrg-slot--absorb mrg-slot--solo">
                <span className="mrg-slot__label">Sesión OPU a eliminar</span>
                <span className="mrg-slot__hint">Se apaga junto con toda su cadena</span>
                {session ? (
                    <div className="mrg-picked">
                        <div className="mrg-picked__body">
                            <span className="mrg-picked__name">{session.code}</span>
                            <span className="mrg-picked__sub">{[formatDateOnly(session.date), session.farm?.name].filter(Boolean).join(' · ') || `#${session.id}`}</span>
                        </div>
                        <button type="button" className="mrg-picked__clear" onClick={() => setSession(null)} aria-label="Quitar selección"><IconClose /></button>
                    </div>
                ) : (
                    <OpuSearch onSelect={setSession} />
                )}
            </div>

            {session && (blocked ? (
                <div className="mrg-preview">
                    <div className="mrg-preview__head">
                        <span className="mrg-badge mrg-badge--block">Bloqueado · {births} parto(s)</span>
                    </div>
                    <p className="mrg-preview__foot">
                        No se puede eliminar: hay <strong>{births} parto(s)</strong> aguas abajo. La cría es un animal real y el
                        borrado es irreversible desde el nacimiento (409). <strong>Nada se modificó.</strong>
                    </p>
                </div>
            ) : (
                <div className="mrg-preview">
                    <div className="mrg-preview__head">
                        <span className="mrg-badge mrg-badge--block">Cascada</span>
                        <span className="mrg-preview__total">{total} registro(s) se apagarían</span>
                    </div>
                    {rows.length > 0 ? (
                        <ul className="mrg-bars">
                            {rows.map(([label, n]) => (
                                <li key={label} className="mrg-bar">
                                    <span className="mrg-bar__label">{label}</span>
                                    <span className="mrg-bar__track"><span className="mrg-bar__fill mrg-bar__fill--danger" style={{ width: `${Math.round(n / maxCount * 100)}%` }} /></span>
                                    <span className="mrg-bar__count">{n}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mrg-preview__foot">La sesión no tiene descendencia registrada; sólo se dará de baja ella.</p>
                    )}
                    <div className="mrg-preview__actions">
                        <button type="button" className="mrg-btn mrg-btn--danger" onClick={() => setConfirming(true)}>Eliminar sesión…</button>
                    </div>
                </div>
            ))}

            {confirming && session && (
                <DeleteOpuModal
                    session={session}
                    onClose={() => setConfirming(false)}
                    onDeleted={() => { setSession(null); setConfirming(false); }}
                />
            )}
        </div>
    );
}
